// DashboardMiegraph.jsx - Monthly Income vs Expenses with Pie Charts (Secure Version)
import React, { useState, useEffect } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import supabase from '../../supabase';
import './DashboardMiegraph.css';

const DashboardMiegraph = ({ user, selectedPeriod, onPeriodChange }) => {
  const [chartData, setChartData] = useState({
    incomeData: [],
    expenseData: [],
    totalIncome: 0,
    totalExpenses: 0,
    netBalance: 0
  });
  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  // Colors for pie charts
  const INCOME_COLORS = ['#10B981', '#059669', '#047857', '#065F46', '#064E3B'];
  const EXPENSE_COLORS = ['#EF4444', '#DC2626', '#B91C1C', '#991B1B', '#7F1D1D'];

  // Development mode check
  const isDevelopment = process.env.NODE_ENV === 'development';

  useEffect(() => {
    if (user && user.id) {
      fetchMIEData();
    } else {
      if (isDevelopment) console.log('No user or user.id provided');
      setLoading(false);
    }
  }, [user, selectedPeriod]);

  const fetchMIEData = async () => {
    try {
      setLoading(true);
      setHasError(false);

      if (isDevelopment) {
        console.log('=== FETCH START ===');
        console.log('Selected Period:', selectedPeriod);
      }

      const [incomeData, expensesData] = await Promise.all([
        fetchIncomeData(),
        fetchExpensesData()
      ]);

      if (isDevelopment) {
        console.log('=== DATA FETCHED ===');
        console.log('Income Records Count:', incomeData?.length || 0);
        console.log('Expense Records Count:', expensesData?.length || 0);
      }

      processChartData(incomeData, expensesData);

    } catch (error) {
      console.error('Error fetching MIE data:', error.message);
      setHasError(true);
      
      setChartData({
        incomeData: [],
        expenseData: [],
        totalIncome: 0,
        totalExpenses: 0,
        netBalance: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchIncomeData = async () => {
    try {
      if (isDevelopment) {
        console.log('Fetching income data...');
      }

      const { data, error } = await supabase
        .from('income')
        .select('*')
        .eq('user_id', user.id);

      if (error) {
        console.error('Error fetching income:', error.message);
        return [];
      }
      
      if (isDevelopment) {
        console.log(`Successfully fetched ${data?.length || 0} income records`);
      }
      
      return data || [];
    } catch (error) {
      console.error('Exception in fetchIncomeData:', error.message);
      return [];
    }
  };

  const fetchExpensesData = async () => {
    try {
      if (isDevelopment) {
        console.log('Fetching expenses data...');
      }

      const { data, error } = await supabase
        .from('expenses')
        .select('*')
        .eq('user_id', user.id);

      if (error) {
        console.error('Error fetching expenses:', error.message);
        return [];
      }
      
      if (isDevelopment) {
        console.log(`Successfully fetched ${data?.length || 0} expense records`);
      }
      
      return data || [];
    } catch (error) {
      console.error('Exception in fetchExpensesData:', error.message);
      return [];
    }
  };

  const getDateRange = () => {
    const now = new Date();
    let startDate, endDate;

    switch (selectedPeriod) {
      case 'this-month':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
        break;
        
      case 'last-month':
        startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        endDate = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
        break;
        
      case 'this-quarter':
        const currentQuarter = Math.floor(now.getMonth() / 3);
        startDate = new Date(now.getFullYear(), currentQuarter * 3, 1);
        endDate = new Date(now.getFullYear(), currentQuarter * 3 + 3, 0, 23, 59, 59);
        break;
        
      case 'last-quarter':
        const lastQuarter = Math.floor(now.getMonth() / 3) - 1;
        if (lastQuarter < 0) {
          startDate = new Date(now.getFullYear() - 1, 9, 1);
          endDate = new Date(now.getFullYear() - 1, 11, 31, 23, 59, 59);
        } else {
          startDate = new Date(now.getFullYear(), lastQuarter * 3, 1);
          endDate = new Date(now.getFullYear(), lastQuarter * 3 + 3, 0, 23, 59, 59);
        }
        break;
        
      case 'this-year':
        startDate = new Date(now.getFullYear(), 0, 1);
        endDate = new Date(now.getFullYear(), 11, 31, 23, 59, 59);
        break;
        
      case 'last-year':
        startDate = new Date(now.getFullYear() - 1, 0, 1);
        endDate = new Date(now.getFullYear() - 1, 11, 31, 23, 59, 59);
        break;
        
      case 'last-3-months':
        startDate = new Date(now.getFullYear(), now.getMonth() - 2, 1);
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
        break;
        
      case 'last-6-months':
        startDate = new Date(now.getFullYear(), now.getMonth() - 5, 1);
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
        break;
        
      case 'all-time':
        startDate = null;
        endDate = null;
        break;
        
      default:
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
    }

    if (isDevelopment) {
      console.log('Date range:', {
        selectedPeriod,
        startDate: startDate?.toLocaleDateString(),
        endDate: endDate?.toLocaleDateString()
      });
    }

    return { startDate, endDate };
  };

  const processChartData = (incomeRecords, expenseRecords) => {
    if (isDevelopment) {
      console.log('=== PROCESSING DATA ===');
    }

    const safeIncomeRecords = Array.isArray(incomeRecords) ? incomeRecords : [];
    const safeExpenseRecords = Array.isArray(expenseRecords) ? expenseRecords : [];

    const { startDate, endDate } = getDateRange();

    // Process income data
    let filteredIncome = safeIncomeRecords;
    
    if (selectedPeriod !== 'all-time' && startDate && endDate) {
      filteredIncome = safeIncomeRecords.filter(income => {
        if (!income.income_date) return false;
        const incomeDate = new Date(income.income_date);
        return incomeDate >= startDate && incomeDate <= endDate;
      });
    }

    // Group income by name
    const incomeByName = filteredIncome.reduce((acc, income) => {
      const name = income.name || 'Other Income';
      const amount = parseFloat(income.total_amount || 0);
      acc[name] = (acc[name] || 0) + amount;
      return acc;
    }, {});

    const incomeData = Object.entries(incomeByName).map(([name, value]) => ({
      name,
      value,
      percentage: 0
    }));

    // Process expense data
    let filteredExpenses = safeExpenseRecords;
    
    if (selectedPeriod !== 'all-time' && startDate && endDate) {
      filteredExpenses = safeExpenseRecords.filter(expense => {
        if (!expense.expense_date) return false;
        const expenseDate = new Date(expense.expense_date);
        return expenseDate >= startDate && expenseDate <= endDate;
      });
    }

    // Group expenses by category
    const expensesByCategory = filteredExpenses.reduce((acc, expense) => {
      const category = expense.category || 'Other Expenses';
      const amount = parseFloat(expense.amount || 0);
      acc[category] = (acc[category] || 0) + amount;
      return acc;
    }, {});

    const expenseData = Object.entries(expensesByCategory).map(([name, value]) => ({
      name,
      value,
      percentage: 0
    }));

    // Calculate totals
    const totalIncome = incomeData.reduce((sum, item) => sum + item.value, 0);
    const totalExpenses = expenseData.reduce((sum, item) => sum + item.value, 0);

    if (isDevelopment) {
      console.log('=== FINAL CALCULATIONS ===');
      console.log('Total Income:', totalIncome);
      console.log('Total Expenses:', totalExpenses);
      console.log('Income Categories:', incomeData.length);
      console.log('Expense Categories:', expenseData.length);
    }

    // Calculate percentages
    incomeData.forEach(item => {
      item.percentage = totalIncome > 0 ? ((item.value / totalIncome) * 100).toFixed(1) : 0;
    });

    expenseData.forEach(item => {
      item.percentage = totalExpenses > 0 ? ((item.value / totalExpenses) * 100).toFixed(1) : 0;
    });

    const finalData = {
      incomeData: incomeData.sort((a, b) => b.value - a.value),
      expenseData: expenseData.sort((a, b) => b.value - a.value),
      totalIncome,
      totalExpenses,
      netBalance: totalIncome - totalExpenses
    };
    
    setChartData(finalData);
  };

  const CustomTooltip = ({ active, payload, label, coordinate }) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      return (
        <div 
          className="dashboardmiegraph-tooltip"
          style={{
            position: 'fixed',
            left: coordinate?.x || 0,
            top: (coordinate?.y || 0) - 60,
            pointerEvents: 'none',
            zIndex: 1000
          }}
        >
          <p className="dashboardmiegraph-tooltip-label">{data.payload.name}</p>
          <p className="dashboardmiegraph-tooltip-value">
            ₹{data.value.toLocaleString()} ({data.payload.percentage}%)
          </p>
        </div>
      );
    }
    return null;
  };

  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    if (percent < 0.05) return null;
    
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text 
        x={x} 
        y={y} 
        fill="#fff" 
        textAnchor={x > cx ? 'start' : 'end'} 
        dominantBaseline="central"
        fontSize="12"
        fontWeight="600"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  if (loading) {
    return (
      <div className="dashboardmiegraph">
        <div className="dashboardmiegraph-loading">
          <div className="dashboardmiegraph-loading-spinner"></div>
          <p>Loading income vs expenses data...</p>
        </div>
      </div>
    );
  }

  if (hasError) {
    return (
      <div className="dashboardmiegraph">
        <div className="dashboardmiegraph-error">
          <p>Failed to load income vs expenses data. Please try again.</p>
          <button onClick={fetchMIEData} className="dashboardmiegraph-retry-btn">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboardmiegraph">
      <div className="dashboardmiegraph-section">
        <div className="dashboardmiegraph-section-header">
          <h2 className="dashboardmiegraph-section-title">Income vs Expenses</h2>
        </div>

        <div className="dashboardmiegraph-summary">
          <div className="dashboardmiegraph-summary-card dashboardmiegraph-income-card">
            <h3 className="dashboardmiegraph-summary-label">Total Income</h3>
            <p className="dashboardmiegraph-summary-amount">₹{chartData.totalIncome.toLocaleString()}</p>
          </div>
          
          <div className="dashboardmiegraph-summary-card dashboardmiegraph-expense-card">
            <h3 className="dashboardmiegraph-summary-label">Total Expenses</h3>
            <p className="dashboardmiegraph-summary-amount">₹{chartData.totalExpenses.toLocaleString()}</p>
          </div>
          
          <div className={`dashboardmiegraph-summary-card ${chartData.netBalance >= 0 ? 'dashboardmiegraph-positive' : 'dashboardmiegraph-negative'}`}>
            <h3 className="dashboardmiegraph-summary-label">Net Balance</h3>
            <p className="dashboardmiegraph-summary-amount">₹{chartData.netBalance.toLocaleString()}</p>
          </div>
        </div>

        <div className="dashboardmiegraph-charts">
          <div className="dashboardmiegraph-chart-container">
            <h3 className="dashboardmiegraph-chart-title">Income Breakdown</h3>
            {chartData.incomeData.length > 0 ? (
              <div className="dashboardmiegraph-chart-wrapper">
                <ResponsiveContainer width="100%" height={400}>
                  <PieChart>
                    <Pie
                      data={chartData.incomeData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={renderCustomizedLabel}
                      outerRadius={150}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {chartData.incomeData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={INCOME_COLORS[index % INCOME_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      content={<CustomTooltip />} 
                      position={{ x: 0, y: 0 }}
                      allowEscapeViewBox={{ x: true, y: true }}
                      offset={10}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="dashboardmiegraph-no-data">
                <p>No income data available for the selected period</p>
              </div>
            )}
          </div>

          <div className="dashboardmiegraph-chart-container">
            <h3 className="dashboardmiegraph-chart-title">Expense Breakdown</h3>
            {chartData.expenseData.length > 0 ? (
              <div className="dashboardmiegraph-chart-wrapper">
                <ResponsiveContainer width="100%" height={400}>
                  <PieChart>
                    <Pie
                      data={chartData.expenseData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={renderCustomizedLabel}
                      outerRadius={150}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {chartData.expenseData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={EXPENSE_COLORS[index % EXPENSE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      content={<CustomTooltip />} 
                      position={{ x: 0, y: 0 }}
                      allowEscapeViewBox={{ x: true, y: true }}
                      offset={10}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="dashboardmiegraph-no-data">
                <p>No expense data available for the selected period</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardMiegraph;